import { z } from 'zod';

import type { ApiUpdateStop } from '#api/model/types.ts';
import { mapStopToUpdateStop } from '#api/stopApi.slice.ts';
import type { Location } from '#domain/Location.ts';
import type { Stop } from '#domain/Stop.ts';
import { error as orderFormError } from '#features/orderForm/model/OrderFormValidation.ts';
import {
    composeDateTimeValue,
    isValidDateValue,
    isValidTimeValue,
    splitDateTimeValue,
} from '#features/orderForm/model/timeWindowFields.ts';
import { parseGeoCoordinateNumber } from '#utils/geoParsing.util.ts';

const requiredString = z.string().min(1, orderFormError.SMALL_STRING);
const requiredDateString = z.string().min(1, orderFormError.INVALID_DATE);

const LocationSchema = z.object({
    street: requiredString,
    city: requiredString,
    zipCode: requiredString,
    country: requiredString,
    latitude: z.number().optional(),
    longitude: z.number().optional(),
});

export const getEditStopFormValidation = (allowCoordinateOnly = false) =>
    z
        .object({
            name: requiredString,
            location: allowCoordinateOnly
                ? LocationSchema.extend({
                      street: z.string(),
                      city: z.string(),
                      zipCode: z.string(),
                      country: z.string(),
                  })
                : LocationSchema,
            startDate: requiredDateString.refine(isValidDateValue, { message: orderFormError.INVALID_DATE }),
            startTime: z.string().refine(isValidTimeValue, { message: orderFormError.INVALID_DATE }).optional(),
            endDate: requiredDateString.refine(isValidDateValue, { message: orderFormError.INVALID_DATE }),
            endTime: z.string().refine(isValidTimeValue, { message: orderFormError.INVALID_DATE }).optional(),
            comment: z.string().optional(),
            latitude: allowCoordinateOnly ? z.number().finite().min(-90).max(90) : z.number().optional(),
            longitude: allowCoordinateOnly ? z.number().finite().min(-180).max(180) : z.number().optional(),
        })
        .refine(
            stop => {
                const startTarget = composeDateTimeValue(stop.startDate, stop.startTime);
                const endTarget = composeDateTimeValue(stop.endDate, stop.endTime);
                return !startTarget || !endTarget || startTarget <= endTarget;
            },
            { path: ['endDate'], message: orderFormError.WINDOW_DATE_ORDER }
        );

export const EditStopFormValidation = getEditStopFormValidation();

export type EditStopFormData = z.infer<typeof EditStopFormValidation>;

export const mapStopToEditStopFormData = (stop: Stop): EditStopFormData => {
    const startTarget = splitDateTimeValue(stop.startTarget);
    const endTarget = splitDateTimeValue(stop.endTarget);

    return {
        name: stop.name,
        startDate: startTarget.date,
        startTime: startTarget.time,
        endDate: endTarget.date,
        endTime: endTarget.time,
        comment: stop.comment,
        latitude: parseGeoCoordinateNumber(stop.latitude),
        longitude: parseGeoCoordinateNumber(stop.longitude),
        location: {
            ...stop.location,
            latitude: parseGeoCoordinateNumber(stop.location.latitude),
            longitude: parseGeoCoordinateNumber(stop.location.longitude),
        },
    };
};

const mapFormLocationToDomainLocation = (location: EditStopFormData['location']): Location => ({
    street: location.street,
    city: location.city,
    zipCode: location.zipCode,
    country: location.country,
    latitude: location.latitude !== undefined ? String(location.latitude) : undefined,
    longitude: location.longitude !== undefined ? String(location.longitude) : undefined,
});

export const mapEditStopFormDataToStop = (formData: EditStopFormData, stop: Stop): Stop => ({
    ...stop,
    name: formData.name,
    location: mapFormLocationToDomainLocation(formData.location),
    startTarget: composeDateTimeValue(formData.startDate, formData.startTime),
    endTarget: composeDateTimeValue(formData.endDate, formData.endTime),
    comment: formData.comment,
    latitude: formData.latitude !== undefined ? String(formData.latitude) : undefined,
    longitude: formData.longitude !== undefined ? String(formData.longitude) : undefined,
});

export const mapEditStopFormDataToUpdateStop = (formData: EditStopFormData, stop: Stop): ApiUpdateStop =>
    mapStopToUpdateStop(mapEditStopFormDataToStop(formData, stop));
